import { useState, useCallback, useRef } from 'react';

/**
 * Interface for pagination token management
 */
export interface PaginationTokens {
  /** Map of page numbers to their corresponding tokens */
  tokens: Map<number, string | null>;
  /** Add or update a token for a specific page */
  setToken: (page: number, token: string | null) => void;
  /** Get token for a specific page */
  getToken: (page: number) => string | null;
  /** Clear all stored tokens */
  clearTokens: () => void;
  /** Get token for previous page navigation */
  getPreviousToken: (currentPage: number) => string | null;
  /** Get token for next page navigation */
  getNextToken: (currentPage: number) => string | null;
  /** Get optimal navigation parameters for a page */
  getNavigationParams: (targetPage: number, pageSize: number, currentPage: number, direction: 'next' | 'previous' | 'jump' | 'first') => PaginationRequestParams;
}

/**
 * Custom hook for managing pagination tokens
 * 
 * This hook manages the storage and retrieval of pagination tokens
 * for API endpoints that use token-based pagination.
 * 
 * @returns PaginationTokens object with token management functions
 */
/**
 * Enhanced pagination tokens hook with pageNumber fallback logic
 * Uses useRef for stable token storage to avoid useEffect dependency issues
 */
export function usePaginationTokens(): PaginationTokens {
  const tokensRef = useRef<Map<number, string | null>>(new Map());
  const [, forceUpdate] = useState<number>(0);

  const setToken = useCallback((page: number, token: string | null) => {
    tokensRef.current.set(page, token);
  }, []);

  const getToken = useCallback((page: number): string | null => {
    return tokensRef.current.get(page) || null;
  }, []);

  const clearTokens = useCallback(() => {
    tokensRef.current = new Map();
    forceUpdate(prev => prev + 1); // Force update if needed
  }, []);

  const getPreviousToken = useCallback((currentPage: number): string | null => {
    return tokensRef.current.get(currentPage - 1) || null;
  }, []);

  const getNextToken = useCallback((currentPage: number): string | null => {
    return tokensRef.current.get(currentPage + 1) || null;
  }, []);

  const getNavigationParams = useCallback((targetPage: number, pageSize: number, _currentPage: number, direction: 'next' | 'previous' | 'jump' | 'first'): PaginationRequestParams => {
    const storedToken = tokensRef.current.get(targetPage);
    
    if (targetPage === 1) {
      return {
        page: targetPage,
        pageSize,
        method: 'firstPage',
        direction
      };
    }
    
    if (storedToken) {
      return {
        page: targetPage,
        pageSize,
        token: storedToken,
        method: 'token',
        direction
      };
    }
    
    return {
      page: targetPage,
      pageSize,
      pageNumber: targetPage,
      method: 'pageNumber',
      direction
    };
  }, []);

  return {
    tokens: tokensRef.current,
    setToken,
    getToken,
    clearTokens,
    getPreviousToken,
    getNextToken,
    getNavigationParams
  };
}

/**
 * Interface for pagination request parameters
 */
export interface PaginationRequestParams {
  /** Target page number */
  page: number;
  /** Page size */
  pageSize: number;
  /** Token to use for request (if available) */
  token?: string | null;
  /** Page number to use for request (fallback) */
  pageNumber?: number;
  /** Method being used */
  method: 'token' | 'pageNumber' | 'firstPage';
  /** Direction of navigation */
  direction: 'next' | 'previous' | 'jump' | 'first';
}

/**
 * Interface for the enhanced pagination props
 */
export interface EnhancedPaginationProps {
  /** Current active page (1-based) */
  currentPage: number;
  /** Total number of pages available */
  totalPages: number;
  /** Page size for requests */
  pageSize: number;
  /** Token for the next page (from API response) */
  nextPageToken?: string | null;
  /** Callback function called when page changes */
  onPageChange: (params: PaginationRequestParams) => void;
  /** Maximum number of page buttons to show (default: 5) */
  maxVisiblePages?: number;
  /** Whether to show previous/next arrows (default: true) */
  showArrows?: boolean;
  /** Custom class name for styling */
  className?: string;
  /** Whether to use token-based navigation (default: false) */
  useTokens?: boolean;
}
